/**
 * Which go-live checklist steps OverviewPage shows -- W1 of shared-services
 * batch 2.
 *
 * A phone-only tenant (account type B: receptionist, no website support) has
 * no widget to install, so the existing 5-step web checklist
 * (`activationProgress.ts`'s `deriveActivationSteps`) must not be shown to
 * them -- it would ask them to do something that does not apply to their
 * account. Every other case (A, C, an undecided trial, and unknown/unreadable
 * services) keeps the existing web checklist untouched: "existing web
 * customers keep identical behaviour unless the brief explicitly says
 * otherwise," and the brief only calls out B.
 */

function activeFlag(section) {
  return section && typeof section === "object" ? section.active : undefined;
}

/**
 * @param {unknown} services - `EntitlementsServices | null`.
 * @returns {"A" | "B" | "C" | "trial" | "unknown"}
 */
export function accountType(services) {
  if (!services || typeof services !== "object") return "unknown";
  const web = activeFlag(services.web_support);
  const rec = activeFlag(services.receptionist);
  if (typeof web !== "boolean" || typeof rec !== "boolean") return "unknown";
  if (web && rec) return "C";
  if (web) return "A";
  if (rec) return "B";
  // Neither is active right now. Batch-4 final review (I-F1-1): a tenant who
  // never purchased the website AND once had a receptionist subscription
  // that has since ended is a B tenant whose receptionist ended, NOT an
  // undecided trial -- they never asked for the website, so the website
  // dashboard and full nav are a dead end for them. `receptionist.status`
  // (unlike `web_support`, which carries no such field) survives past its
  // own `active` flag flipping to false, so it is the one signal available
  // for "this was ever a phone account". A tenant whose WEBSITE was ever
  // purchased (its `plan` is not the default "trial") is left as "trial"
  // here even if both sides are now lapsed -- that is the rarer
  // both-services-ended case, and ServiceEndedBanner's own "both" copy
  // covers it without forcing it through the B-only nav and checklist.
  const receptionistStatus = services.receptionist && typeof services.receptionist === "object"
    ? services.receptionist.status
    : null;
  const webSupport = services.web_support && typeof services.web_support === "object"
    ? services.web_support
    : null;
  // F1 fix round 1 (m1): `plan !== "trial"` alone is a DEAD signal --
  // src/billing/gate.py's `tenant_plan()` collapses any non-active/past_due
  // web plan status back to "trial", so a lapsed website subscription can
  // never report anything but "trial" through this field once it lapses.
  // `ever_subscribed` (added to GET /api/billing/entitlements this round,
  // src/billing/entitlements.py's `resolve_services`) is the real, raw
  // signal: it is derived from `tenants.plan` directly, which a
  // cancellation never resets (src/billing/webhook_handler.py's
  // `_handle_subscription_deleted` passes `plan=None`). The old check is
  // kept as a fallback for a `services` payload from before this field
  // existed -- harmless, since it can only ever agree or say less.
  const websiteEverPurchased = webSupport?.ever_subscribed === true
    || (typeof webSupport?.plan === "string" && webSupport.plan !== "trial");
  if (!websiteEverPurchased && receptionistEverPurchased(receptionistStatus)) return "B";
  return "trial";
}

/**
 * Statuses `services.receptionist.status` reports for a subscription that
 * was granted and has since lapsed -- as opposed to "trial"/null (never
 * purchased) or "incomplete" (F1 fix round 1, m2: Stripe's PaymentIntent
 * never completed, so NOTHING was ever granted -- treating it as "ever
 * purchased" told a tenant who never successfully paid that their
 * [nonexistent] subscription had "ended").
 */
export const RECEPTIONIST_LAPSED_STATUSES = new Set(["canceled", "paused", "unpaid"]);

/**
 * Whether `status` reflects a receptionist relationship that was ever
 * actually granted -- currently active/trialing/past_due, or lapsed from one
 * of those. Shared by `goLiveChecklist.mjs`, `serviceEnded.mjs` and
 * `addServiceOffer.mjs` so the three can never disagree about what "ever
 * purchased" means (F1 fix round 1, m2).
 * @param {string | null | undefined} status
 * @returns {boolean}
 */
export function receptionistEverPurchased(status) {
  if (status == null) return false;
  if (status === "active" || status === "trialing" || status === "past_due") return true;
  return RECEPTIONIST_LAPSED_STATUSES.has(status);
}

/**
 * @template T
 * @param {unknown} services
 * @param {T[]} webSteps - the existing activation steps (A/trial/unknown).
 * @param {T[]} receptionistSteps - shown for account type B, and appended
 *   after `webSteps` for account type C (finding 4, batch-4 final review:
 *   the comment this replaces said "B or C" while the code only ever
 *   checked B).
 * @returns {T[]}
 */
export function selectGoLiveSteps(services, webSteps, receptionistSteps) {
  const type = accountType(services);
  if (type === "B") return receptionistSteps;
  if (type === "C") return [...webSteps, ...receptionistSteps];
  return webSteps;
}

/**
 * Business-profile completeness, from `GET /api/business-profile`'s
 * projection (`name`/`hours`/`services`/`faqs` -- see
 * `src/business_profile/projection.py`). "Complete enough" means the two
 * facts a receptionist cannot do without are both approved: a business name
 * and its hours. `profile` is the `{name, hours, services, faqs, ...}` object
 * the route returns under `result.profile`, each field a `{value, source,
 * approved}` fact; a malformed or missing profile reads as incomplete.
 * @param {unknown} profile
 * @returns {boolean}
 */
export function businessProfileComplete(profile) {
  if (!profile || typeof profile !== "object") return false;
  const approved = (key) => {
    const fact = profile[key];
    return !!fact && typeof fact === "object" && fact.approved === true && fact.value !== null;
  };
  return approved("name") && approved("hours");
}

/**
 * Receptionist-relevant go-live steps for account type B, built from data the
 * app already fetches: `GET /api/receptionist/status`'s draft
 * (`ReceptionistDraft` in shared/api.ts) and `GET /api/business-profile`'s
 * projection. Shaped like `ActivationStep` (`activationProgress.ts`) so
 * OverviewPage can render either list with the same component.
 *
 * @param {{
 *   businessProfileReady: boolean,
 *   draftExists: boolean,
 *   criticalGapsCount: number,
 *   configurationGenerated: boolean,
 *   providerSynced: boolean,
 * }} facts
 * @returns {{label: string, detail: string, href: string, complete: boolean}[]}
 */
export function deriveReceptionistGoLiveSteps(facts) {
  return [
    {
      label: "Complete your business profile",
      detail: "Name and hours, approved for the receptionist to use.",
      // The Business profile editor on Settings: saves drafts and lets the
      // owner approve them. Settings is a shared nav destination, so a
      // phone-only tenant can reach it (review I7).
      href: "/settings#business-profile",
      complete: !!facts.businessProfileReady,
    },
    {
      label: "Start your receptionist draft",
      detail: "Bring in your business facts and provider knowledge.",
      href: "/receptionist",
      complete: !!facts.draftExists,
    },
    {
      label: "Resolve critical gaps",
      detail: "Facts the receptionist cannot go live without.",
      href: "/receptionist",
      complete: !!facts.draftExists && (facts.criticalGapsCount || 0) === 0,
    },
    {
      label: "Generate your receptionist configuration",
      detail: "Turns your approved facts into a proposed greeting and call flow.",
      href: "/receptionist",
      complete: !!facts.configurationGenerated,
    },
    {
      label: "Sync your draft with the provider",
      detail: "Creates or updates the provider-side draft agent.",
      href: "/receptionist",
      complete: !!facts.providerSynced,
    },
  ];
}
